import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import { Row, Col, Title, SubTitle, Text, Button } from "../theme";
import { checkStoredOrPromptPassword } from "../utilities/passwordCookie";

const imageStyle = {
  background: "white",
  boxSizing: "border-box",
  maxHeight: "520px",
  maxWidth: "100%",
  objectFit: "contain",
  padding: "20px",
  width: "100%",
};

const memeDetailsStyle = {
  overflowWrap: "anywhere",
  wordBreak: "break-word",
};

const inputStyle = {
  border: "3px solid white",
  color: "#5956a1",
  fontSize: "1.1em",
  margin: "10px",
  padding: "10px",
};

const tagGroupsStyle = {
  display: "grid",
  gap: "12px",
  gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
};

const tagGroupStyle = {
  border: "1px solid rgba(255, 255, 255, 0.35)",
  padding: "8px",
};

const tagGroupTitleStyle = {
  color: "white",
  fontSize: "0.85em",
  fontWeight: "bold",
  margin: "0 0 6px",
  textTransform: "uppercase",
};

const tagListStyle = {
  alignItems: "flex-start",
  display: "flex",
  flexWrap: "wrap",
  gap: "5px",
};

const tagChipStyle = {
  background: "#5956a1",
  border: "1px solid white",
  color: "white",
  cursor: "pointer",
  fontSize: "0.78em",
  lineHeight: 1.1,
  padding: "4px 6px",
};

const selectedTagChipStyle = {
  ...tagChipStyle,
  background: "white",
  color: "#5956a1",
};

const linkStyle = {
  color: "white",
  textDecoration: "underline",
};

const linkButtonStyle = {
  background: "transparent",
  border: 0,
  color: "white",
  cursor: "pointer",
  font: "inherit",
  padding: 0,
  textDecoration: "underline",
};

const SANITY_MEME_URL =
  "https://studio.massiveexoplanetmemeexhibition.com/desk/meme;";

const getTagCount = (meme, savedTagIdsByMeme) =>
  savedTagIdsByMeme[meme._id]?.length || meme.taxonomy?.length || 0;

const getSelectedTagIds = (meme, savedTagIdsByMeme) =>
  savedTagIdsByMeme[meme._id] || meme.taxonomy?.map((tag) => tag._id) || [];

const Tagger = () => {
  const history = useHistory();
  const { memeId } = useParams();
  const [settings, memes, tags, client, editions] = useSelector((state) => [
    state.data.settings,
    state.data.memes || [],
    state.data.tags || [],
    state.data.client,
    state.data.editions || [],
  ]);
  const [secure, setSecure] = useState(null);
  const [selectedEdition, setSelectedEdition] = useState("all");
  const [maxTags, setMaxTags] = useState(3);
  const [currentMemeId, setCurrentMemeId] = useState(null);
  const [selectedTagIds, setSelectedTagIds] = useState([]);
  const [savedTagIdsByMeme, setSavedTagIdsByMeme] = useState({});
  const [status, setStatus] = useState(null);
  const [idInput, setIdInput] = useState("");

  useEffect(() => {
    if (secure === null && settings) {
      setSecure(checkStoredOrPromptPassword(settings.password));
    }
  }, [secure, settings]);

  const editionNames = useMemo(() => {
    const names =
      editions.length > 0
        ? editions.map(({ edition }) => edition)
        : [...new Set(memes.map((meme) => meme.edition).filter(Boolean))];
    return names.sort((a, b) => Number(a) - Number(b));
  }, [editions, memes]);

  const tagGroups = useMemo(() => {
    const tagById = {};
    tags.forEach((tag) => {
      tagById[tag._id] = tag;
    });

    const usedTagIds = new Set();
    const grouped =
      settings?.selectedTagsGrouped?.map((group) => {
        const groupTags = group.selectedTags
          .map(({ _ref }) => tagById[_ref])
          .filter(Boolean);

        groupTags.forEach((tag) => usedTagIds.add(tag._id));

        return {
          categoryTitle: group.categoryTitle,
          tags: groupTags,
        };
      }) || [];

    const otherTags = tags.filter((tag) => !usedTagIds.has(tag._id));
    if (otherTags.length > 0) {
      grouped.push({
        categoryTitle: "Other",
        tags: otherTags,
      });
    }

    return grouped;
  }, [settings, tags]);

  const editionMemes = useMemo(
    () =>
      memes
        .filter((meme) => {
          if (!meme.img) {
            return false;
          }

          return selectedEdition === "all" || meme.edition === selectedEdition;
        })
        .sort((a, b) => (a._createdAt || "").localeCompare(b._createdAt || "")),
    [memes, selectedEdition]
  );

  const matchingMemes = useMemo(
    () =>
      editionMemes.filter(
        (meme) => getTagCount(meme, savedTagIdsByMeme) < Number(maxTags)
      ),
    [editionMemes, maxTags, savedTagIdsByMeme]
  );

  const currentMeme = useMemo(
    () => memes.find((meme) => meme._id === currentMemeId),
    [memes, currentMemeId]
  );

  const selectMeme = (meme) => {
    if (memeId) {
      history.push("/tagger");
    }

    if (!meme) {
      setCurrentMemeId(null);
      setSelectedTagIds([]);
      return;
    }

    setCurrentMemeId(meme._id);
    setSelectedTagIds(getSelectedTagIds(meme, savedTagIdsByMeme));
    setStatus(null);
  };

  const selectRandomMeme = () => {
    selectMeme(
      matchingMemes[Math.floor(Math.random() * matchingMemes.length)] || null
    );
  };

  const findNextMeme = () => {
    if (editionMemes.length === 0) {
      return null;
    }

    const currentIndex = editionMemes.findIndex(
      (meme) => meme._id === currentMemeId
    );

    for (let step = 1; step <= editionMemes.length; step += 1) {
      const meme =
        editionMemes[(currentIndex + step) % editionMemes.length];

      if (
        meme._id !== currentMemeId &&
        getTagCount(meme, savedTagIdsByMeme) < Number(maxTags)
      ) {
        return meme;
      }
    }

    return null;
  };

  const loadMemeById = () => {
    const id = idInput.trim();
    if (!id) {
      return;
    }
    setIdInput("");
    history.push(`/tagger/${id}`);
  };

  useEffect(() => {
    if (!secure || !memeId || memes.length === 0) {
      return;
    }

    const linkedMeme = memes.find((meme) => meme._id === memeId);
    if (!linkedMeme) {
      setCurrentMemeId(null);
      setSelectedTagIds([]);
      return;
    }

    setCurrentMemeId(linkedMeme._id);
    setSelectedTagIds(linkedMeme.taxonomy?.map((tag) => tag._id) || []);
    setStatus(null);
  }, [secure, memeId, memes]);

  useEffect(() => {
    if (secure && !memeId && !currentMeme && matchingMemes.length > 0) {
      selectRandomMeme();
    }
  });

  const toggleTag = (tagId) => {
    setSelectedTagIds((current) =>
      current.includes(tagId)
        ? current.filter((selectedTagId) => selectedTagId !== tagId)
        : [...current, tagId]
    );
  };

  const saveTags = () => {
    if (!client || !currentMeme || status === "saving") {
      return Promise.resolve();
    }

    setStatus("saving");
    return client
      .updateMemeTags(currentMeme._id, selectedTagIds)
      .then(() => {
        setSavedTagIdsByMeme((current) => ({
          ...current,
          [currentMeme._id]: selectedTagIds,
        }));
        setStatus("saved");
      })
      .catch((error) => {
        console.error(error);
        setStatus("error");
      });
  };

  if (!settings || !client) {
    return null;
  }

  if (secure === false) {
    return (
      <Row center="xs">
        <Col xs={12}>
          <Title>TAGGER</Title>
          <Text>Wrong password.</Text>
        </Col>
      </Row>
    );
  }

  if (!secure) {
    return null;
  }

  return (
    <>
      <Row center="xs">
        <Col xs={12}>
          <Title>TAGGER</Title>
        </Col>
      </Row>

      <Row>
        <Col xs={12}>
          <SubTitle>Criteria</SubTitle>
          <select
            style={inputStyle}
            value={selectedEdition}
            onChange={(event) => {
              setSelectedEdition(event.target.value);
              setCurrentMemeId(null);
            }}
          >
            <option value="all">All editions</option>
            {editionNames.map((edition) => (
              <option key={edition} value={edition}>
                {edition}
              </option>
            ))}
          </select>
          <input
            min="1"
            aria-label="Less than amount of tags"
            style={inputStyle}
            type="number"
            value={maxTags}
            onChange={(event) => {
              setMaxTags(event.target.value);
              setCurrentMemeId(null);
            }}
          />
          <Button noUrl text="Random" action={selectRandomMeme} />
          <br />
          <Button
            noUrl
            text="Load latest meme"
            action={() => selectMeme(matchingMemes[matchingMemes.length - 1])}
          />
          <Button
            noUrl
            text="Load oldest meme"
            action={() => selectMeme(matchingMemes[0])}
          />
          <br />
          <input
            aria-label="Load meme by sanity id"
            placeholder="Paste sanity id"
            style={inputStyle}
            type="text"
            value={idInput}
            onChange={(event) => setIdInput(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                loadMemeById();
              }
            }}
          />
          <Button noUrl text="Load" action={loadMemeById} />
          <Text>
            Edition: {selectedEdition}. Less than {maxTags} tags.{" "}
            {matchingMemes.length} matching memes.
            {memeId ? " Direct meme link active." : null}
          </Text>
        </Col>
      </Row>

      {currentMeme ? (
        <>
          <Row>
            <Col xs={12} md={6}>
              {currentMeme.img ? (
                <img
                  src={currentMeme.img}
                  alt={currentMeme.description || currentMeme._id}
                  style={imageStyle}
                />
              ) : (
                <Text>No image available.</Text>
              )}
            </Col>
            <Col xs={12} md={6} style={memeDetailsStyle}>
              <SubTitle>{currentMeme.description || currentMeme._id}</SubTitle>
              <Text>
                <button
                  type="button"
                  style={linkButtonStyle}
                  onClick={() =>
                    navigator.clipboard?.writeText(currentMeme._id)
                  }
                >
                  copy id to clipboard
                </button>
                {" | "}
                <a
                  href={`${SANITY_MEME_URL}${currentMeme._id}`}
                  rel="noopener noreferrer"
                  style={linkStyle}
                  target="_blank"
                >
                  link to studio
                </a>
                <br />
                Edition: {currentMeme.edition || "none"}
                <br />
                Tags: {selectedTagIds.length}
              </Text>
              <Button noUrl text="Save" action={saveTags} />
              <Button
                noUrl
                text="Save + Random"
                action={() => {
                  saveTags().then(() => {
                    if (memeId) {
                      history.push("/tagger");
                    }
                    setCurrentMemeId(null);
                  });
                }}
              />
              <Button
                noUrl
                text="Save + Next"
                action={() => {
                  const nextMeme = findNextMeme();
                  saveTags().then(() => selectMeme(nextMeme));
                }}
              />
              {status === "saving" && <Text>Saving...</Text>}
              {status === "saved" && <Text>Saved.</Text>}
              {status === "error" && <Text>Could not save tags.</Text>}
            </Col>
          </Row>

          <Row>
            <Col xs={12}>
              <SubTitle>Tags</SubTitle>
              <div style={tagGroupsStyle}>
                {tagGroups.map(({ categoryTitle, tags: groupTags }) => (
                  <div key={categoryTitle} style={tagGroupStyle}>
                    <div style={tagGroupTitleStyle}>{categoryTitle}</div>
                    <div style={tagListStyle}>
                      {groupTags.map((tag) => {
                        const selected = selectedTagIds.includes(tag._id);

                        return (
                          <button
                            key={tag._id}
                            type="button"
                            style={
                              selected ? selectedTagChipStyle : tagChipStyle
                            }
                            onClick={() => toggleTag(tag._id)}
                          >
                            {tag.name}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </Col>
          </Row>
        </>
      ) : (
        <Row>
          <Col xs={12}>
            <Text>No meme matches the current criteria.</Text>
          </Col>
        </Row>
      )}
    </>
  );
};

export default Tagger;
